import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ChevronRight } from "lucide-react";
import { api } from "@/api/client";
import { logger } from "@/utils/logger";

const RecommendedForYou = ({ user }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [basedOn, setBasedOn] = useState([]);

  useEffect(() => {
    fetchRecommendations();
  }, [user]);

  const fetchRecommendations = async () => {
    try {
      const res = await api.get("/recommendations?limit=8");
      setRecommendations(res.data.recommendations || []);
      setBasedOn(res.data.based_on_species || []);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || recommendations.length === 0) return null;

  return (
    <section className="py-12 px-6 md:px-12" data-testid="recommended-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <div>
              <h2 className="text-xl text-[#EDEDED]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Recommended for You
              </h2>
              {basedOn.length > 0 && (
                <p className="text-xs text-[#71717A] mt-0.5">
                  Based on your interest in {basedOn.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(", ")}
                </p>
              )}
            </div>
          </div>
          <Link
            to="/search"
            className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors flex items-center gap-1"
          >
            Browse All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {recommendations.slice(0, 8).map((listing) => (
            <Link
              key={listing.listing_id}
              to={`/listing/${listing.listing_id}`}
              className="glass-card overflow-hidden hover:border-[#D4AF37]/30 transition-all group"
              data-testid={`rec-card-${listing.listing_id}`}
            >
              <div className="relative h-36 bg-[#18181B]">
                {listing.photos?.[0] ? (
                  <img
                    src={listing.photos[0]}
                    alt={listing.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-3xl text-[#3F3F46]">
                    {listing.species === "equine" ? "🐴" : listing.species === "canine" ? "🐕" : listing.species === "feline" ? "🐈" : "🐾"}
                  </div>
                )}
                {listing.is_auction && (
                  <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-[#D4AF37]/90 text-black text-xs rounded font-medium">
                    Auction
                  </div>
                )}
              </div>
              <div className="p-3">
                <p className="text-sm text-[#EDEDED] truncate font-medium">{listing.title}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-xs text-[#71717A] capitalize">{listing.species}</span>
                  <span className="text-sm text-[#D4AF37] font-semibold">
                    ${listing.price?.toLocaleString()}
                  </span>
                </div>
                {listing.breeder_name && (
                  <p className="text-xs text-[#52525B] mt-1 truncate">{listing.breeder_name}</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RecommendedForYou;
